import { createRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { publicLayoutRoute } from '@/ui/pages/public'
import { useGetSiteIdentity, useGetSocial } from '@/application/services/settings.service'
import { useCurrentTheme } from '@/application/hooks/useCurrentTheme'
import { useDocumentMeta } from '@/application/hooks/useDocumentMeta'
import { OG_IMAGE_URL } from '@/application/seo/site'
import { useLocalizedUrl } from '@/application/hooks/useLocalizedUrl'
import { Breadcrumb } from '@/ui/components/common/Breadcrumb'
import { BreadcrumbJsonLd } from '@/ui/components/public/BreadcrumbJsonLd'
import type { SocialSettings } from '@/domains/types'

export const publicAboutRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: 'about',
  component: PublicAboutPage,
})

type SocialKey = keyof Omit<SocialSettings, 'updatedAt' | 'footerLinks'>

// --- Inline SVG icons for each social platform ---

interface IconProps {
  className?: string
}

function XIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  )
}

function BlueSkyIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.04.415-.056-.14.017-.279.036-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.558-4.073 1.082-6.498-2.83-7.078a8.741 8.741 0 0 1-.415-.056c.14.016.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.479 0-.689-.139-1.86-.902-2.203-.66-.299-1.664-.621-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z" />
    </svg>
  )
}

function FacebookIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function InstagramIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
    </svg>
  )
}

function TikTokIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  )
}

function YoutubeIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

function TwitchIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
    </svg>
  )
}

function DiscordIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z" />
    </svg>
  )
}

// Switch-based resolver — exhaustive over SocialKey, no array typing complexity

function SocialNetworkIcon({
  platform,
  className,
}: {
  platform: SocialKey
  className?: string
}): React.JSX.Element {
  switch (platform) {
    case 'xUrl':
      return <XIcon className={className} />
    case 'blueskyUrl':
      return <BlueSkyIcon className={className} />
    case 'facebookUrl':
      return <FacebookIcon className={className} />
    case 'instagramUrl':
      return <InstagramIcon className={className} />
    case 'tiktokUrl':
      return <TikTokIcon className={className} />
    case 'youtubeUrl':
      return <YoutubeIcon className={className} />
    case 'twitchUrl':
      return <TwitchIcon className={className} />
    case 'discordUrl':
      return <DiscordIcon className={className} />
  }
}

const SOCIAL_LABELS: Array<{ key: SocialKey; label: string }> = [
  { key: 'xUrl', label: 'X' },
  { key: 'blueskyUrl', label: 'BlueSky' },
  { key: 'facebookUrl', label: 'Facebook' },
  { key: 'instagramUrl', label: 'Instagram' },
  { key: 'tiktokUrl', label: 'TikTok' },
  { key: 'youtubeUrl', label: 'YouTube' },
  { key: 'twitchUrl', label: 'Twitch' },
  { key: 'discordUrl', label: 'Discord' },
]

const SUPPORTED_LANG_CODES = ['fr', 'en', 'es']

function resolveLanguage(inputLang: string): string {
  const code = inputLang.substring(0, 2).toLowerCase()
  return SUPPORTED_LANG_CODES.includes(code) ? code : 'fr'
}

export function PublicAboutPage() {
  const { t, i18n } = useTranslation()
  const lang = resolveLanguage(i18n.language)
  const { data: siteIdentity } = useGetSiteIdentity(lang)
  const { data: social } = useGetSocial()
  const theme = useCurrentTheme()

  const effectiveName = siteIdentity?.siteName || t('app_name')
  const effectiveDesc =
    siteIdentity?.siteDescription || t('about.description', { appName: effectiveName })

  const activeSocials: Array<{ key: SocialKey; label: string; url: string }> = SOCIAL_LABELS.reduce<
    Array<{ key: SocialKey; label: string; url: string }>
  >((result, { key: socialKey, label }) => {
    if (!social) return result
    const urlValue = social[socialKey]
    if (typeof urlValue === 'string' && urlValue.length > 0) {
      result.push({ key: socialKey, label, url: urlValue })
    }
    return result
  }, [])

  const pageUrl = useLocalizedUrl('/about')

  useDocumentMeta({
    title: t('seo.about_title'),
    description: t('seo.about_description'),
    imageUrl: OG_IMAGE_URL,
    url: pageUrl,
    siteName: effectiveName,
    type: 'website',
  })

  const breadcrumbItems = [{ label: t('nav.home'), href: '/' }, { label: t('nav.about') }]

  return (
    <div className="container m-auto w-full max-w-[100rem] bg-card grow px-4 py-16 text-center">
      <BreadcrumbJsonLd items={breadcrumbItems} currentUrl={pageUrl} />
      <Breadcrumb items={breadcrumbItems} className="text-left" />
      {/* Logo + site name — centred */}
      <div className="flex flex-col items-center gap-4 mb-10">
        <img src={theme.logoUrl} alt={effectiveName} className="h-24 w-24 object-contain" />
        <h1 className="text-3xl font-serif font-bold text-center">{effectiveName}</h1>
      </div>

      {/* Description — left-aligned */}
      <p className="text-muted-foreground leading-relaxed">{effectiveDesc}</p>

      {/* Social links */}
      {activeSocials.length > 0 && (
        <div className="mt-10 flex flex-col items-center gap-3">
          <p className="text-sm font-medium text-muted-foreground">{t('about.follow_us')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {activeSocials.map((item) => (
              <a
                key={item.label}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted/50 transition-colors"
              >
                <SocialNetworkIcon platform={item.key} className="w-4 h-4 shrink-0" />
                {item.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
